import Phaser from "phaser";
import { goTo } from "../ui/sceneTransition";
import { BOTTOM_BAR_HEIGHT, GAME_HEIGHT, GAME_WIDTH, TILE_SIZE, TOP_BAR_HEIGHT } from "../config";
import { buildMapFromDef, getCoverProjection, pixelToTile, tileCenter, type MapData } from "../map/MapData";
import { MapRenderer } from "../map/MapRenderer";
import { CAMPAIGN_MAPS } from "../maps/maps";
import {
  clearPlacementMask,
  defaultPlacementMask,
  exportCustomMasks,
  loadCustomPlacementMask,
  savePlacementMask,
} from "../systems/PlacementMasks";
import {
  clearPathOverride,
  exportCustomPaths,
  loadPathOverride,
  savePathOverride,
} from "../systems/PathOverrides";
import {
  clearFoliageOcclusions,
  exportCustomFoliage,
  isFoliageArea,
  loadFoliageOcclusions,
  saveFoliageOcclusions,
  type FoliageZone,
} from "../systems/FoliageOcclusions";
import { button, panel, THEME } from "../ui/theme";

interface EditorCell {
  bg: Phaser.GameObjects.Rectangle;
  icon: Phaser.GameObjects.Image;
}

type EditorMode = "mask" | "path" | "foliage";

/** Drafts with the ALT endpoint snapped onto the MAIN route (raw drafts stay untouched). */
interface MergeResult {
  main: [number, number][];
  alt: [number, number][];
  merged: boolean;
  joinPx?: { x: number; y: number };
}

// World-pixel radius in which an ALT endpoint snaps onto the MAIN route.
const MERGE_SNAP_PX = 42;
// Snapping this close to an existing MAIN vertex reuses it instead of splitting the segment.
const VERTEX_REUSE_PX = 8;
// Minimum squared world-pixel distance between drag-sampled points (keeps curves clean).
const MIN_DRAW_DIST_SQ = 24 * 24;
// Foliage brush radius bounds and default (world px).
const FOLIAGE_RADIUS_MIN = 16;
const FOLIAGE_RADIUS_MAX = 100;
const FOLIAGE_RADIUS_DEFAULT = 46;
const FOLIAGE_RADIUS_STEP = 8;

/** In-game authoring tool for tower placement masks, enemy path overrides, and foliage occlusion zones. */
export class MapEditorScene extends Phaser.Scene {
  private mapIndex = 0;
  private editorMode: EditorMode = "mask";

  // ── Mask editor state ──
  private map!: MapData;
  private mask: boolean[][] = [];
  private cells: EditorCell[][] = [];
  private custom = false;
  private paintValue: boolean | null = null;
  private statusText?: Phaser.GameObjects.Text;
  private countText?: Phaser.GameObjects.Text;
  private previewTower?: Phaser.GameObjects.Image;
  private towerButtonLabel?: Phaser.GameObjects.Text;

  // ── Path editor state ──
  private lane: "main" | "alt" = "main";
  private draftMain: [number, number][] = [];
  private draftAlt: [number, number][] = [];
  private pathGfx?: Phaser.GameObjects.Graphics;
  private drawSurface?: Phaser.GameObjects.Rectangle;
  private pathStatusText?: Phaser.GameObjects.Text;
  private laneToggleLabel?: Phaser.GameObjects.Text;

  // ── Foliage editor state ──
  private mapRenderer!: MapRenderer;
  private draftFoliage: FoliageZone[] = [];
  private foliageShape: "brush" | "area" = "area";
  private pendingArea: [number, number][] = [];
  private foliageRadius = FOLIAGE_RADIUS_DEFAULT;
  private foliageGfx?: Phaser.GameObjects.Graphics;
  private foliageBrushWorld?: { x: number; y: number };
  private lastFoliageDragWorld?: { x: number; y: number };
  private foliageStatusText?: Phaser.GameObjects.Text;
  private foliageShapeLabel?: Phaser.GameObjects.Text;
  // Editor-only towers used to preview how a zone will occlude. Never saved.
  private mockTowers: Phaser.GameObjects.Image[] = [];

  // ── Shared UI refs ──
  private modeToggleLabel?: Phaser.GameObjects.Text;
  private exportBtnLabel?: Phaser.GameObjects.Text;
  private titleText?: Phaser.GameObjects.Text;
  private subtitleText?: Phaser.GameObjects.Text;
  private maskButtons: Phaser.GameObjects.GameObject[] = [];
  private pathButtons: Phaser.GameObjects.GameObject[] = [];
  private foliageButtons: Phaser.GameObjects.GameObject[] = [];
  private overlay?: Phaser.GameObjects.Container;

  constructor() { super("MapEditorScene"); }

  init(data?: { mapIndex?: number; mode?: EditorMode }): void {
    // Returning from a PLAY TEST run passes no data, so fall back to the registry.
    const fromRegistryIndex = this.registry.get("editorMapIndex") as number | undefined;
    const fromRegistryMode = this.registry.get("editorMode") as EditorMode | undefined;
    this.mapIndex = Phaser.Math.Wrap(data?.mapIndex ?? fromRegistryIndex ?? this.mapIndex, 0, CAMPAIGN_MAPS.length);
    this.editorMode = data?.mode ?? fromRegistryMode ?? "mask";
    this.registry.set("editorPlayTest", false);
  }

  create(): void {
    const def = CAMPAIGN_MAPS[this.mapIndex];
    this.map = buildMapFromDef(def);
    // Remembered so MenuScene → MAP EDITOR and PLAY TEST both land back here.
    this.registry.set("editorMapIndex", this.mapIndex);
    this.registry.set("editorMode", this.editorMode);

    // Reset mask state
    const saved = loadCustomPlacementMask(def.key, this.map.cols, this.map.rows);
    this.custom = saved !== null;
    this.mask = saved ?? defaultPlacementMask(this.map);
    this.cells = [];
    this.previewTower = undefined;
    this.towerButtonLabel = undefined;
    this.paintValue = null;

    // Reset path state; seed drafts from stored override or source map def
    this.lane = "main";
    const existingOverride = loadPathOverride(def.key);
    this.draftMain = existingOverride?.main ? [...existingOverride.main] : def.paintedWaypoints ? [...def.paintedWaypoints] : [];
    this.draftAlt = existingOverride?.alt ? [...existingOverride.alt] : def.altPaintedWaypoints ? [...def.altPaintedWaypoints] : [];
    this.pathGfx = undefined;
    this.drawSurface = undefined;
    this.maskButtons = [];
    this.pathButtons = [];
    this.foliageButtons = [];
    this.overlay = undefined;

    // Reset foliage state; seed draft from stored override or source map def
    this.draftFoliage = loadFoliageOcclusions(def.key) ?? (def.foliageOcclusions ? [...def.foliageOcclusions] : []);
    this.foliageShape = "area";
    this.pendingArea = [];
    this.foliageRadius = FOLIAGE_RADIUS_DEFAULT;
    this.foliageGfx = undefined;
    this.foliageBrushWorld = undefined;
    this.lastFoliageDragWorld = undefined;
    this.mockTowers = [];

    this.mapRenderer = new MapRenderer(this, this.map, def.biome, def.backgroundImage ?? `map-preview-${def.key}`);
    this.mapRenderer.draw();
    this.add.rectangle(0, this.map.offsetY, GAME_WIDTH, this.map.rows * this.map.tileSize, 0x0b1014, 0.12)
      .setOrigin(0)
      .setDepth(-5);

    this.buildGrid();
    this.buildTopBar(def.name);
    this.buildBottomBar();
    this.refreshStatus();

    if (this.editorMode === "path") this.enterPathMode();
    else if (this.editorMode === "foliage") this.enterFoliageMode();

    this.installKeyboard();
  }

  /** Keyboard shortcuts. ESC closes an open overlay first, then leaves the editor. */
  private installKeyboard(): void {
    const stopPainting = () => { this.paintValue = null; };
    const kb = this.input.keyboard;

    const handlers: Record<string, () => void> = {
      "keydown-LEFT":  () => this.openMap(this.mapIndex - 1),
      "keydown-RIGHT": () => this.openMap(this.mapIndex + 1),
      "keydown-ESC":   () => { if (this.overlay) this.closeOverlay(); else goTo(this, "MenuScene"); },
      "keydown-M":     () => this.toggleMode(),
      "keydown-L":     () => { if (this.editorMode === "path") this.toggleLane(); },
      "keydown-B":     () => { if (this.editorMode === "foliage") this.toggleFoliageShape(); },
      "keydown-ENTER": () => { if (this.editorMode === "foliage") this.finishFoliageArea(); },
      "keydown-T":     () => { if (this.editorMode === "foliage") this.addMockTower(); },
      "keydown-P":     () => this.playTest(),
      "keydown-H":     () => this.showHelp(),
      "keydown-O":     () => this.showMapPicker(),
    };

    this.input.on("pointerup", stopPainting);
    for (const [evt, fn] of Object.entries(handlers)) kb?.on(evt, fn);

    // Ctrl+Z undoes the last point in whichever freeform mode is active.
    const onUndo = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== "z") return;
      if (this.editorMode === "path") { e.preventDefault(); this.undoLastPoint(); }
      else if (this.editorMode === "foliage") { e.preventDefault(); this.undoFoliageSpot(); }
    };
    window.addEventListener("keydown", onUndo);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.input.off("pointerup", stopPainting);
      for (const [evt, fn] of Object.entries(handlers)) kb?.off(evt, fn);
      window.removeEventListener("keydown", onUndo);
    });
  }

  // ── Grid (mask mode) ──────────────────────────────────────────────────────

  private buildGrid(): void {
    for (let r = 0; r < this.map.rows; r++) {
      this.cells[r] = [];
      for (let c = 0; c < this.map.cols; c++) {
        const x = this.map.offsetX + c * this.map.tileSize;
        const y = this.map.offsetY + r * this.map.tileSize;
        const bg = this.add.rectangle(x + 2, y + 2, this.map.tileSize - 4, this.map.tileSize - 4)
          .setOrigin(0)
          .setInteractive({ useHandCursor: true });
        const icon = this.add.image(x + this.map.tileSize / 2, y + this.map.tileSize / 2, "tower-archer-1")
          .setDisplaySize(this.map.tileSize * 0.72, this.map.tileSize * 0.72)
          .setAlpha(0.85);

        bg.on("pointerdown", () => {
          this.paintValue = !this.mask[r][c];
          this.setCell(c, r, this.paintValue);
        });
        bg.on("pointerover", (pointer: Phaser.Input.Pointer) => {
          if (pointer.isDown && this.paintValue !== null) this.setCell(c, r, this.paintValue);
          else bg.setStrokeStyle(3, THEME.hex.gold, 1);
        });
        bg.on("pointerout", () => this.refreshCell(c, r));
        this.cells[r][c] = { bg, icon };
        this.refreshCell(c, r);
      }
    }
  }

  // ── Top Bar ───────────────────────────────────────────────────────────────

  private buildTopBar(mapName: string): void {
    panel(this, 0, 0, GAME_WIDTH, TOP_BAR_HEIGHT + 12, "iron-bar").setDepth(THEME.depths.hud);

    // Title doubles as the map picker trigger — click it to jump to any map.
    this.titleText = this.add.text(GAME_WIDTH / 2, 15, `PLACEMENT EDITOR  •  ${mapName}  ▾`, {
      fontFamily: THEME.fonts.display, fontSize: "19px", color: THEME.colors.gold,
    }).setOrigin(0.5).setDepth(THEME.depths.hud + 1).setInteractive({ useHandCursor: true });
    this.titleText.on("pointerover", () => this.titleText?.setColor(THEME.colors.parchment));
    this.titleText.on("pointerout", () => this.titleText?.setColor(THEME.colors.gold));
    this.titleText.on("pointerdown", () => this.showMapPicker());

    this.subtitleText = this.add.text(GAME_WIDTH / 2, 38, "archer = tower allowed   •   plain tile = blocked   •   click or drag to paint", {
      fontFamily: THEME.fonts.body, fontSize: "13px", color: THEME.colors.parchmentDim,
    }).setOrigin(0.5).setDepth(THEME.depths.hud + 1);

    button(this, 34, 26, 48, 36, "‹", {
      fontSize: 16, onClick: () => this.openMap(this.mapIndex - 1),
    }).setDepth(THEME.depths.hud + 2);
    button(this, GAME_WIDTH - 34, 26, 48, 36, "›", {
      fontSize: 16, onClick: () => this.openMap(this.mapIndex + 1),
    }).setDepth(THEME.depths.hud + 2);

    button(this, 100, 26, 78, 34, "MAPS  O", {
      fontSize: 11, onClick: () => this.showMapPicker(),
    }).setDepth(THEME.depths.hud + 2);

    const modeBtn = button(this, 218, 26, 156, 34, "MODE: MASK", {
      fontSize: 12, onClick: () => this.toggleMode(),
    }).setDepth(THEME.depths.hud + 2);
    this.modeToggleLabel = modeBtn.getData("label") as Phaser.GameObjects.Text;

    const towerBtn = button(this, 380, 26, 140, 34, "PLACE TOWER", {
      fontSize: 11, onClick: () => this.toggleTowerPreview(),
    }).setDepth(THEME.depths.hud + 2);
    this.towerButtonLabel = towerBtn.getData("label") as Phaser.GameObjects.Text;
    this.maskButtons.push(towerBtn);

    const exportBtn = button(this, GAME_WIDTH - 148, 26, 140, 34, "EXPORT MASKS", {
      fontSize: 11, onClick: () => this.handleExport(),
    }).setDepth(THEME.depths.hud + 2);
    this.exportBtnLabel = exportBtn.getData("label") as Phaser.GameObjects.Text;

    button(this, GAME_WIDTH - 244, 26, 44, 34, "? H", {
      fontSize: 11, onClick: () => this.showHelp(),
    }).setDepth(THEME.depths.hud + 2);
  }

  // ── Overlays (map picker / help) ──────────────────────────────────────────

  /** Overlays disable the path draw surface so their clicks can't leak into the route. */
  private openOverlay(c: Phaser.GameObjects.Container): void {
    this.drawSurface?.disableInteractive();
    this.overlay = c;
  }

  private closeOverlay(): void {
    if (!this.overlay) return;
    this.overlay.destroy();
    this.overlay = undefined;
    // Deferred a tick so the closing click's pointerup can't reach the surface.
    this.time.delayedCall(0, () => this.drawSurface?.setInteractive({ useHandCursor: true }));
  }

  /** Full list of campaign maps; click any entry to jump straight to it. */
  private showMapPicker(): void {
    if (this.overlay) { this.closeOverlay(); return; }

    const c = this.add.container(0, 0).setDepth(THEME.depths.overlay);
    const dim = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.7)
      .setOrigin(0).setInteractive();
    dim.on("pointerdown", () => this.closeOverlay());
    c.add(dim);

    const rows = CAMPAIGN_MAPS.length;
    const rowH = 44, padY = 74;
    const panelW = 460, panelH = padY + rows * rowH + 40;
    const px = (GAME_WIDTH - panelW) / 2;
    const py = (GAME_HEIGHT - panelH) / 2;
    c.add(panel(this, px, py, panelW, panelH, "parchment"));
    c.add(this.add.text(GAME_WIDTH / 2, py + 30, "SELECT MAP", {
      fontFamily: THEME.fonts.display, fontSize: "22px", color: THEME.colors.iron,
    }).setOrigin(0.5));

    CAMPAIGN_MAPS.forEach((def, i) => {
      const y = py + padY + i * rowH;
      const active = i === this.mapIndex;
      const hasPath = loadPathOverride(def.key) !== null;
      const hasMask = loadCustomPlacementMask(def.key, this.map.cols, this.map.rows) !== null;
      const hasFoliage = loadFoliageOcclusions(def.key) !== null;

      const base = active ? 0x3a2a14 : 0x000000;
      const baseAlpha = active ? 0.9 : 0.10;
      const row = this.add.rectangle(px + 18, y, panelW - 36, rowH - 6, base, baseAlpha)
        .setOrigin(0).setInteractive({ useHandCursor: true });
      const label = this.add.text(px + 34, y + (rowH - 6) / 2, `${i + 1}.  ${def.name}`, {
        fontFamily: THEME.fonts.display, fontSize: "16px",
        color: active ? THEME.colors.gold : THEME.colors.iron,
      }).setOrigin(0, 0.5);

      const badges = [hasPath ? "PATH" : "", hasMask ? "MASK" : "", hasFoliage ? "FOLIAGE" : ""].filter(Boolean).join(" + ");
      const badge = this.add.text(px + panelW - 34, y + (rowH - 6) / 2, badges, {
        fontFamily: THEME.fonts.body, fontSize: "12px",
        color: active ? THEME.colors.frost : THEME.colors.goldDim,
      }).setOrigin(1, 0.5);

      row.on("pointerover", () => {
        row.setFillStyle(0x3a2a14, active ? 1 : 0.55);
        label.setColor(THEME.colors.gold);
        badge.setColor(THEME.colors.frost);
      });
      row.on("pointerout", () => {
        row.setFillStyle(base, baseAlpha);
        label.setColor(active ? THEME.colors.gold : THEME.colors.iron);
        badge.setColor(active ? THEME.colors.frost : THEME.colors.goldDim);
      });
      row.on("pointerdown", () => { this.closeOverlay(); if (!active) this.openMap(i); });

      c.add([row, label, badge]);
    });

    c.add(this.add.text(GAME_WIDTH / 2, py + panelH - 20, "ESC or click outside to close", {
      fontFamily: THEME.fonts.body, fontSize: "12px", color: THEME.colors.goldDim,
    }).setOrigin(0.5));

    this.openOverlay(c);
  }

  private showHelp(): void {
    if (this.overlay) { this.closeOverlay(); return; }

    const lines = [
      "SHORTCUTS",
      "",
      "O            open map picker",
      "← / →        previous / next map",
      "M            cycle MASK / PATH / FOLIAGE mode",
      "L            toggle MAIN / ALT lane (path mode)",
      "B            toggle AREA / BRUSH shape (foliage mode)",
      "ENTER        finish the current area (foliage mode)",
      "T            drop a draggable test tower (foliage mode)",
      "Ctrl+Z       undo last path point / foliage point",
      "P            play test the current map",
      "H            this help",
      "ESC          close overlay / back to menu",
      "",
      "MASK MODE — click or drag tiles to allow/block tower placement.",
      "PATH MODE — left-click adds a waypoint, drag sketches a curve,",
      "right-click removes the last point. Drawing autosaves.",
      "ALT lane ending near a MAIN vertex snaps to it and merges;",
      "otherwise it runs independently to its own castle gate.",
      "",
      "FOLIAGE MODE — mark zones where the painted map art is redrawn",
      "above towers AND enemies, so units there look like they stand",
      "behind painted foliage. AREA shape draws a free-form outline just",
      "like the path (click or drag, ENTER/FINISH closes it, 3+ points).",
      "BRUSH shape stamps circles; the mouse wheel resizes it.",
      "Right-click undoes. Only visible on maps with a painted background.",
      "T / + TOWER drops a draggable test tower that sorts exactly like a",
      "real one, so you can shape a zone around it. Drag to move, right-",
      "click it to remove, CLR TOWERS clears them. They are never saved.",
      "",
      "PLAY TEST runs the map — quitting returns you straight back here.",
    ];

    const c = this.add.container(0, 0).setDepth(THEME.depths.overlay);
    const dim = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.7)
      .setOrigin(0).setInteractive();
    dim.on("pointerdown", () => this.closeOverlay());
    c.add(dim);

    const panelW = 620, panelH = 664;
    const px = (GAME_WIDTH - panelW) / 2;
    const py = (GAME_HEIGHT - panelH) / 2;
    c.add(panel(this, px, py, panelW, panelH, "parchment"));
    c.add(this.add.text(px + 36, py + 28, lines.join("\n"), {
      fontFamily: THEME.fonts.body, fontSize: "15px", color: THEME.colors.iron, lineSpacing: 4,
    }));
    c.add(button(this, GAME_WIDTH / 2, py + panelH - 34, 140, 38, "CLOSE", {
      display: true, fontSize: 14, onClick: () => this.closeOverlay(),
    }));

    this.openOverlay(c);
  }

  // ── Bottom Bar ────────────────────────────────────────────────────────────

  private buildBottomBar(): void {
    const y = GAME_HEIGHT - BOTTOM_BAR_HEIGHT;
    panel(this, 0, y, GAME_WIDTH, BOTTOM_BAR_HEIGHT, "iron-bar").setDepth(THEME.depths.hud);

    // Mask buttons
    const backMask = button(this, 72, y + 40, 120, 46, "BACK", {
      display: true, fontSize: 15, onClick: () => goTo(this, "MenuScene"),
    }).setDepth(THEME.depths.hud + 1);
    const all1 = button(this, 218, y + 40, 140, 46, "ALL 1", {
      display: true, fontSize: 14, onClick: () => this.fill(true),
    }).setDepth(THEME.depths.hud + 1);
    const all0 = button(this, 374, y + 40, 140, 46, "ALL 0", {
      display: true, fontSize: 14, onClick: () => this.fill(false),
    }).setDepth(THEME.depths.hud + 1);
    const resetDef = button(this, 558, y + 40, 180, 46, "RESET DEFAULT", {
      display: true, fontSize: 13, onClick: () => this.resetDefault(),
    }).setDepth(THEME.depths.hud + 1);
    const playTestMask = button(this, GAME_WIDTH - 94, y + 40, 164, 46, "PLAY TEST", {
      display: true, fontSize: 14, onClick: () => this.playTest(),
    }).setDepth(THEME.depths.hud + 1);
    this.maskButtons.push(backMask, all1, all0, resetDef, playTestMask);

    this.statusText = this.add.text(680, y + 23, "", {
      fontFamily: THEME.fonts.display, fontSize: "13px", color: THEME.colors.gold,
    }).setDepth(THEME.depths.hud + 1);
    this.countText = this.add.text(680, y + 48, "", {
      fontFamily: THEME.fonts.body, fontSize: "13px", color: THEME.colors.parchmentDim,
    }).setDepth(THEME.depths.hud + 1);

    // Path buttons — laid out left-to-right with a running cursor so they never overlap.
    let cursor = 12;
    const pathBtn = (w: number, label: string, fontSize: number, onClick: () => void) => {
      const b = button(this, cursor + w / 2, y + 40, w, 46, label, { display: true, fontSize, onClick })
        .setDepth(THEME.depths.hud + 1).setVisible(false);
      cursor += w + 8;
      return b;
    };

    const backPath = pathBtn(84, "BACK", 13, () => goTo(this, "MenuScene"));
    const laneBtn = pathBtn(128, "LANE: MAIN", 12, () => this.toggleLane());
    this.laneToggleLabel = laneBtn.getData("label") as Phaser.GameObjects.Text;
    const undoBtn = pathBtn(84, "UNDO", 12, () => this.undoLastPoint());
    const clearLaneBtn = pathBtn(126, "CLEAR LANE", 12, () => this.clearCurrentLane());
    const resetPathBtn = pathBtn(126, "RESET PATH", 12, () => this.resetPath());
    const extendSpawnBtn = pathBtn(152, "EXTEND SPAWN", 12, () => this.extendSpawn());
    const playTestPath = button(this, GAME_WIDTH - 94, y + 40, 164, 46, "PLAY TEST", {
      display: true, fontSize: 14, onClick: () => this.playTest(),
    }).setDepth(THEME.depths.hud + 1).setVisible(false);

    // Status sits in its own strip ABOVE the bottom bar so it never collides with buttons.
    const statusY = y - 26;
    const pathStatusBg = this.add.rectangle(GAME_WIDTH / 2, statusY + 12, GAME_WIDTH, 24, 0x0b0805, 0.75)
      .setDepth(THEME.depths.hud).setVisible(false);
    this.pathStatusText = this.add.text(GAME_WIDTH / 2, statusY + 12, "", {
      fontFamily: THEME.fonts.display, fontSize: "13px", color: THEME.colors.gold, align: "center",
    }).setOrigin(0.5).setDepth(THEME.depths.hud + 1).setVisible(false);

    this.pathButtons.push(backPath, laneBtn, undoBtn, clearLaneBtn, resetPathBtn, extendSpawnBtn, playTestPath, pathStatusBg, this.pathStatusText);

    // Foliage buttons — same running-cursor layout as the path buttons above.
    let foliageCursor = 12;
    const foliageBtn = (w: number, label: string, fontSize: number, onClick: () => void) => {
      const b = button(this, foliageCursor + w / 2, y + 40, w, 46, label, { display: true, fontSize, onClick })
        .setDepth(THEME.depths.hud + 1).setVisible(false);
      foliageCursor += w + 8;
      return b;
    };

    const backFoliage = foliageBtn(84, "BACK", 13, () => goTo(this, "MenuScene"));
    const shapeBtn = foliageBtn(140, "SHAPE: AREA", 12, () => this.toggleFoliageShape());
    this.foliageShapeLabel = shapeBtn.getData("label") as Phaser.GameObjects.Text;
    const finishAreaBtn = foliageBtn(126, "FINISH ↵", 12, () => this.finishFoliageArea());
    const brushMinusBtn = foliageBtn(84, "BRUSH −", 12, () => this.changeFoliageRadius(-FOLIAGE_RADIUS_STEP));
    const brushPlusBtn = foliageBtn(84, "BRUSH +", 12, () => this.changeFoliageRadius(FOLIAGE_RADIUS_STEP));
    const undoFoliageBtn = foliageBtn(84, "UNDO", 12, () => this.undoFoliageSpot());
    const clearFoliageBtn = foliageBtn(90, "CLEAR", 12, () => this.clearFoliageDraft());
    const resetFoliageBtn = foliageBtn(90, "RESET", 12, () => this.resetFoliageDraft());
    const addTowerBtn = foliageBtn(104, "+ TOWER", 12, () => this.addMockTower());
    const clearTowersBtn = foliageBtn(124, "CLR TOWERS", 12, () => this.clearMockTowers());
    const playTestFoliage = button(this, GAME_WIDTH - 94, y + 40, 164, 46, "PLAY TEST", {
      display: true, fontSize: 14, onClick: () => this.playTest(),
    }).setDepth(THEME.depths.hud + 1).setVisible(false);

    const foliageStatusBg = this.add.rectangle(GAME_WIDTH / 2, statusY + 12, GAME_WIDTH, 24, 0x0b0805, 0.75)
      .setDepth(THEME.depths.hud).setVisible(false);
    this.foliageStatusText = this.add.text(GAME_WIDTH / 2, statusY + 12, "", {
      fontFamily: THEME.fonts.display, fontSize: "13px", color: THEME.colors.gold, align: "center",
    }).setOrigin(0.5).setDepth(THEME.depths.hud + 1).setVisible(false);

    this.foliageButtons.push(backFoliage, shapeBtn, finishAreaBtn, brushMinusBtn, brushPlusBtn, undoFoliageBtn, clearFoliageBtn, resetFoliageBtn, addTowerBtn, clearTowersBtn, playTestFoliage, foliageStatusBg, this.foliageStatusText);
  }

  // ── Mode toggle ───────────────────────────────────────────────────────────

  private toggleMode(): void {
    if (this.editorMode === "mask") this.enterPathMode();
    else if (this.editorMode === "path") { this.teardownPathMode(); this.enterFoliageMode(); }
    else { this.teardownFoliageMode(); this.enterMaskMode(); }
  }

  private enterPathMode(): void {
    this.editorMode = "path";
    this.registry.set("editorMode", "path");
    this.modeToggleLabel?.setText("MODE: PATH");
    this.exportBtnLabel?.setText("EXPORT PATHS");
    this.titleText?.setText(`PATH EDITOR  •  ${CAMPAIGN_MAPS[this.mapIndex].name}  ▾`);
    this.subtitleText?.setText("left-click = add point   •   drag = draw curve   •   right-click = undo last   •   H = help");

    for (let r = 0; r < this.map.rows; r++)
      for (let c = 0; c < this.map.cols; c++) {
        this.cells[r][c].bg.disableInteractive().setVisible(false);
        this.cells[r][c].icon.setVisible(false);
      }

    this.setGroupVisible(this.maskButtons, false);
    this.statusText?.setVisible(false);
    this.countText?.setVisible(false);
    this.setGroupVisible(this.pathButtons, true);

    this.input.mouse?.disableContextMenu();

    // Transparent draw surface sits above map, below HUD
    this.drawSurface = this.add.rectangle(
      GAME_WIDTH / 2,
      this.map.offsetY + (this.map.rows * this.map.tileSize) / 2,
      GAME_WIDTH,
      this.map.rows * this.map.tileSize,
      0x000000, 0
    ).setInteractive({ useHandCursor: true }).setDepth(10);

    this.drawSurface.on("pointerdown", (ptr: Phaser.Input.Pointer) => this.handlePathPointerDown(ptr));
    this.drawSurface.on("pointermove", (ptr: Phaser.Input.Pointer) => this.handlePathPointerMove(ptr));
    this.drawSurface.on("pointerup", () => this.savePathDraft());

    this.pathGfx = this.add.graphics().setDepth(12);
    this.redrawPathPreview();
  }

  private teardownPathMode(): void {
    this.drawSurface?.destroy();
    this.drawSurface = undefined;
    this.pathGfx?.destroy();
    this.pathGfx = undefined;
    this.setGroupVisible(this.pathButtons, false);
  }

  private enterMaskMode(): void {
    this.editorMode = "mask";
    this.registry.set("editorMode", "mask");
    this.modeToggleLabel?.setText("MODE: MASK");
    this.exportBtnLabel?.setText("EXPORT MASKS");
    this.titleText?.setText(`PLACEMENT EDITOR  •  ${CAMPAIGN_MAPS[this.mapIndex].name}  ▾`);
    this.subtitleText?.setText("archer = tower allowed   •   plain tile = blocked   •   click or drag to paint   •   H = help");

    for (let r = 0; r < this.map.rows; r++)
      for (let c = 0; c < this.map.cols; c++) {
        this.cells[r][c].bg.setInteractive({ useHandCursor: true }).setVisible(true);
        this.refreshCell(c, r);
      }

    this.setGroupVisible(this.maskButtons, true);
    this.statusText?.setVisible(true);
    this.countText?.setVisible(true);
  }

  private enterFoliageMode(): void {
    this.editorMode = "foliage";
    this.registry.set("editorMode", "foliage");
    this.modeToggleLabel?.setText("MODE: FOLIAGE");
    this.exportBtnLabel?.setText("EXPORT FOLIAGE");
    this.titleText?.setText(`FOLIAGE EDITOR  •  ${CAMPAIGN_MAPS[this.mapIndex].name}  ▾`);
    this.subtitleText?.setText("AREA: click/drag outline, ENTER finishes   •   B = brush   •   T = test tower   •   right-click = undo   •   H = help");

    for (let r = 0; r < this.map.rows; r++)
      for (let c = 0; c < this.map.cols; c++) {
        this.cells[r][c].bg.disableInteractive().setVisible(false);
        this.cells[r][c].icon.setVisible(false);
      }

    this.setGroupVisible(this.maskButtons, false);
    this.statusText?.setVisible(false);
    this.countText?.setVisible(false);
    this.setGroupVisible(this.foliageButtons, true);

    this.input.mouse?.disableContextMenu();

    this.drawSurface = this.add.rectangle(
      GAME_WIDTH / 2,
      this.map.offsetY + (this.map.rows * this.map.tileSize) / 2,
      GAME_WIDTH,
      this.map.rows * this.map.tileSize,
      0x000000, 0
    ).setInteractive({ useHandCursor: true }).setDepth(10);

    this.drawSurface.on("pointerdown", (ptr: Phaser.Input.Pointer) => this.handleFoliagePointerDown(ptr));
    this.drawSurface.on("pointermove", (ptr: Phaser.Input.Pointer) => this.handleFoliagePointerMove(ptr));
    // An unfinished polygon must not be committed on every mouse release.
    this.drawSurface.on("pointerup", () => { if (this.foliageShape === "brush") this.saveFoliageDraft(); });
    this.input.on("wheel", this.onFoliageWheel);

    this.foliageGfx = this.add.graphics().setDepth(12);
    this.redrawFoliagePreview();
  }

  private teardownFoliageMode(): void {
    this.finishFoliageArea();
    this.clearMockTowers();
    this.drawSurface?.destroy();
    this.drawSurface = undefined;
    this.foliageGfx?.destroy();
    this.foliageGfx = undefined;
    this.foliageBrushWorld = undefined;
    this.input.off("wheel", this.onFoliageWheel);
    this.setGroupVisible(this.foliageButtons, false);
  }

  private onFoliageWheel = (_ptr: Phaser.Input.Pointer, _over: unknown, _dx: number, dy: number): void => {
    if (this.foliageShape !== "brush") return;
    this.changeFoliageRadius(dy > 0 ? -FOLIAGE_RADIUS_STEP : FOLIAGE_RADIUS_STEP);
  };

  private setGroupVisible(group: Phaser.GameObjects.GameObject[], visible: boolean): void {
    for (const go of group) {
      const o = go as unknown as { setVisible?: (v: boolean) => void };
      o.setVisible?.(visible);
    }
  }

  // ── Path drawing ──────────────────────────────────────────────────────────

  private worldToNorm(x: number, y: number): [number, number] {
    const proj = getCoverProjection(this.map);
    return [(x - proj.left) / proj.renderWidth, (y - proj.top) / proj.renderHeight];
  }

  private activeDraft(): [number, number][] {
    return this.lane === "main" ? this.draftMain : this.draftAlt;
  }

  private handlePathPointerDown(ptr: Phaser.Input.Pointer): void {
    if (ptr.rightButtonDown()) { this.undoLastPoint(); return; }
    this.activeDraft().push(this.worldToNorm(ptr.worldX, ptr.worldY));
    this.redrawPathPreview();
  }

  private handlePathPointerMove(ptr: Phaser.Input.Pointer): void {
    if (!ptr.isDown || ptr.rightButtonDown()) return;
    const draft = this.activeDraft();
    if (draft.length === 0) return;
    const [lu, lv] = draft[draft.length - 1];
    const proj = getCoverProjection(this.map);
    const lx = proj.left + lu * proj.renderWidth;
    const ly = proj.top + lv * proj.renderHeight;
    const dx = ptr.worldX - lx;
    const dy = ptr.worldY - ly;
    if (dx * dx + dy * dy >= MIN_DRAW_DIST_SQ) {
      draft.push(this.worldToNorm(ptr.worldX, ptr.worldY));
      this.redrawPathPreview();
    }
  }

  private resolveMerge(): MergeResult {
    const main = this.draftMain.slice();
    const alt = this.draftAlt.slice();
    if (alt.length < 2 || main.length < 2) return { main, alt, merged: false };

    const proj = getCoverProjection(this.map);
    const toPx = ([u, v]: [number, number]) => ({
      x: proj.left + u * proj.renderWidth,
      y: proj.top + v * proj.renderHeight,
    });
    const end = toPx(alt[alt.length - 1]);

    // Nearest point on the whole polyline, not the first vertex in range —
    // otherwise a road that doubles back steals the join and enemies backtrack.
    let bestDist = Infinity, bestSeg = -1, bestT = 0;
    for (let i = 0; i < main.length - 1; i++) {
      const a = toPx(main[i]);
      const b = toPx(main[i + 1]);
      const vx = b.x - a.x, vy = b.y - a.y;
      const lenSq = vx * vx + vy * vy;
      const t = lenSq > 0
        ? Math.max(0, Math.min(1, ((end.x - a.x) * vx + (end.y - a.y) * vy) / lenSq))
        : 0;
      const dist = Math.hypot(end.x - (a.x + vx * t), end.y - (a.y + vy * t));
      if (dist < bestDist) { bestDist = dist; bestSeg = i; bestT = t; }
    }
    if (bestSeg < 0 || bestDist >= MERGE_SNAP_PX) return { main, alt, merged: false };

    const a = toPx(main[bestSeg]);
    const b = toPx(main[bestSeg + 1]);
    const segLen = Math.hypot(b.x - a.x, b.y - a.y);

    let joinIdx: number;
    if (bestT * segLen < VERTEX_REUSE_PX) {
      joinIdx = bestSeg;
    } else if ((1 - bestT) * segLen < VERTEX_REUSE_PX) {
      joinIdx = bestSeg + 1;
    } else {
      // Split the segment so the alt lane joins exactly where it met the road.
      const [au, av] = main[bestSeg];
      const [bu, bv] = main[bestSeg + 1];
      joinIdx = bestSeg + 1;
      main.splice(joinIdx, 0, [au + (bu - au) * bestT, av + (bv - av) * bestT]);
    }

    alt[alt.length - 1] = [main[joinIdx][0], main[joinIdx][1]];
    return { main, alt, merged: true, joinPx: toPx(main[joinIdx]) };
  }

  private savePathDraft(): void {
    if (this.draftMain.length < 2) { this.redrawPathPreview(); return; }
    const resolved = this.resolveMerge();

    const override = resolved.alt.length >= 2
      ? { main: resolved.main, alt: resolved.alt }
      : { main: resolved.main };
    savePathOverride(CAMPAIGN_MAPS[this.mapIndex].key, override);

    const prevTileHash = this.map.tiles.flat().join("");
    this.map = buildMapFromDef(CAMPAIGN_MAPS[this.mapIndex]);
    const newTileHash = this.map.tiles.flat().join("");

    const def = CAMPAIGN_MAPS[this.mapIndex];
    if (prevTileHash !== newTileHash && loadCustomPlacementMask(def.key, this.map.cols, this.map.rows)) {
      this.pathStatusText?.setText("PATH CHANGED — REVIEW PLACEMENT MASK").setColor("#ff6600");
      return;
    }

    this.redrawPathPreview();
  }

  private redrawPathPreview(): void {
    if (!this.pathGfx) return;
    this.pathGfx.clear();

    const def = CAMPAIGN_MAPS[this.mapIndex];
    const radius = def.pathBlockRadius ?? TILE_SIZE * 0.78;
    const proj = getCoverProjection(this.map);

    const toPx = (pts: [number, number][]) =>
      pts.map(([u, v]) => ({ x: proj.left + u * proj.renderWidth, y: proj.top + v * proj.renderHeight }));

    const drawLane = (pts: [number, number][], color: number, alpha: number, showVerts: boolean) => {
      if (pts.length < 2) return;
      const px = toPx(pts);

      this.pathGfx!.lineStyle(radius * 2, 0x000000, 0.10 * alpha);
      this.pathGfx!.beginPath();
      this.pathGfx!.moveTo(px[0].x, px[0].y);
      for (let i = 1; i < px.length; i++) this.pathGfx!.lineTo(px[i].x, px[i].y);
      this.pathGfx!.strokePath();

      this.pathGfx!.lineStyle(3, color, 0.9 * alpha);
      this.pathGfx!.beginPath();
      this.pathGfx!.moveTo(px[0].x, px[0].y);
      for (let i = 1; i < px.length; i++) this.pathGfx!.lineTo(px[i].x, px[i].y);
      this.pathGfx!.strokePath();

      this.pathGfx!.fillStyle(0x44ff44, alpha);
      this.pathGfx!.fillCircle(px[0].x, px[0].y, 6);
      this.pathGfx!.fillStyle(0xff4444, alpha);
      this.pathGfx!.fillCircle(px[px.length - 1].x, px[px.length - 1].y, 6);

      if (showVerts) {
        this.pathGfx!.fillStyle(color, 0.7 * alpha);
        for (const p of px) this.pathGfx!.fillCircle(p.x, p.y, 3);
      }
    };

    const mainActive = this.lane === "main";
    const resolved = this.resolveMerge();
    drawLane(resolved.main, 0xffd700, mainActive ? 1 : 0.4, mainActive);
    drawLane(resolved.alt, 0x88ccff, !mainActive ? 1 : 0.4, !mainActive);

    if (resolved.joinPx) {
      this.pathGfx.lineStyle(2, 0xffffff, 0.9);
      this.pathGfx.strokeCircle(resolved.joinPx.x, resolved.joinPx.y, 10);
    }

    const hasSaved = loadPathOverride(CAMPAIGN_MAPS[this.mapIndex].key) !== null;
    const mergeStr = this.draftAlt.length >= 2 ? (resolved.merged ? " (MÜNDET)" : " (EIGENSTÄNDIG)") : "";
    const altStr = this.draftAlt.length >= 2 ? `  —  ALT ${this.draftAlt.length} PKT${mergeStr}` : "";
    const saveStr = hasSaved ? "  —  CUSTOM PATH SAVED" : "";
    const needStr = this.draftMain.length < 2 ? "  NEED 2+ POINTS" : "";
    this.pathStatusText?.setText(`MAIN ${this.draftMain.length} PKT${altStr}${saveStr}${needStr}`).setColor(THEME.colors.gold);
  }

  // ── Foliage drawing ───────────────────────────────────────────────────────

  private handleFoliagePointerDown(ptr: Phaser.Input.Pointer): void {
    if (ptr.rightButtonDown()) { this.undoFoliageSpot(); return; }
    if (this.foliageShape === "area") {
      this.pendingArea.push(this.worldToNorm(ptr.worldX, ptr.worldY));
      this.lastFoliageDragWorld = { x: ptr.worldX, y: ptr.worldY };
      this.redrawFoliagePreview();
      return;
    }
    this.addFoliageSpotAt(ptr.worldX, ptr.worldY);
  }

  private handleFoliagePointerMove(ptr: Phaser.Input.Pointer): void {
    this.foliageBrushWorld = { x: ptr.worldX, y: ptr.worldY };
    if (ptr.isDown && !ptr.rightButtonDown()) {
      const last = this.lastFoliageDragWorld;
      // Areas sketch like the path editor; the brush stamps spaced circles.
      const spacing = this.foliageShape === "area" ? Math.sqrt(MIN_DRAW_DIST_SQ) : this.foliageRadius * 0.6;
      if (!last || (ptr.worldX - last.x) ** 2 + (ptr.worldY - last.y) ** 2 >= spacing * spacing) {
        if (this.foliageShape === "area") {
          this.pendingArea.push(this.worldToNorm(ptr.worldX, ptr.worldY));
          this.lastFoliageDragWorld = { x: ptr.worldX, y: ptr.worldY };
          this.redrawFoliagePreview();
        } else {
          this.addFoliageSpotAt(ptr.worldX, ptr.worldY);
        }
        return;
      }
    }
    this.redrawFoliagePreview();
  }

  private addFoliageSpotAt(wx: number, wy: number): void {
    const [nx, ny] = this.worldToNorm(wx, wy);
    const proj = getCoverProjection(this.map);
    this.draftFoliage.push({ x: nx, y: ny, r: this.foliageRadius / proj.renderWidth });
    this.lastFoliageDragWorld = { x: wx, y: wy };
    this.redrawFoliagePreview();
  }

  private toggleFoliageShape(): void {
    // Committing first keeps a half-drawn area from silently vanishing.
    this.finishFoliageArea();
    this.foliageShape = this.foliageShape === "brush" ? "area" : "brush";
    this.foliageShapeLabel?.setText(`SHAPE: ${this.foliageShape.toUpperCase()}`);
    this.redrawFoliagePreview();
  }

  /** Closes the in-progress polygon into a zone. A polygon needs 3+ points. */
  private finishFoliageArea(): void {
    if (this.pendingArea.length >= 3) {
      this.draftFoliage.push({ points: this.pendingArea });
      this.pendingArea = [];
      this.lastFoliageDragWorld = undefined;
      this.saveFoliageDraft();
      return;
    }
    this.pendingArea = [];
    this.lastFoliageDragWorld = undefined;
    this.redrawFoliagePreview();
  }

  /** Undo removes the in-progress polygon's last point first, then whole zones. */
  private undoFoliageSpot(): void {
    if (this.pendingArea.length > 0) {
      this.pendingArea.pop();
      this.redrawFoliagePreview();
      return;
    }
    if (this.draftFoliage.length > 0) { this.draftFoliage.pop(); this.saveFoliageDraft(); }
  }

  private saveFoliageDraft(): void {
    const def = CAMPAIGN_MAPS[this.mapIndex];
    saveFoliageOcclusions(def.key, this.draftFoliage);
    this.mapRenderer.drawFoliageOcclusions(this.draftFoliage);
    this.redrawFoliagePreview();
  }

  private clearFoliageDraft(): void {
    this.draftFoliage = [];
    this.pendingArea = [];
    this.saveFoliageDraft();
  }

  private resetFoliageDraft(): void {
    const def = CAMPAIGN_MAPS[this.mapIndex];
    clearFoliageOcclusions(def.key);
    this.draftFoliage = def.foliageOcclusions ? [...def.foliageOcclusions] : [];
    this.pendingArea = [];
    this.mapRenderer.drawFoliageOcclusions(this.draftFoliage);
    this.redrawFoliagePreview();
  }

  private changeFoliageRadius(delta: number): void {
    this.foliageRadius = Phaser.Math.Clamp(this.foliageRadius + delta, FOLIAGE_RADIUS_MIN, FOLIAGE_RADIUS_MAX);
    this.redrawFoliagePreview();
  }

  // ── Test towers (foliage mode only) ───────────────────────────────────────

  /**
   * Drops a draggable archer at the cursor (or map centre) purely to preview the
   * occlusion result. It mirrors the real Tower entity's anchor, size and
   * `depth = tileCenterY + tileSize/2` rule so what you see here is what the
   * game renders. Never written to the mask, the map, or any save.
   */
  private addMockTower(): void {
    const fallback = tileCenter(this.map, Math.floor(this.map.cols / 2), Math.floor(this.map.rows / 2));
    const at = this.foliageBrushWorld ?? fallback;
    const img = this.add.image(0, 0, "tower-archer-1")
      .setOrigin(0.5, 1)
      .setDisplaySize(this.map.tileSize * 1.02, this.map.tileSize * 1.02)
      .setInteractive({ useHandCursor: true, draggable: true });
    this.input.setDraggable(img);
    this.snapMockTower(img, at.x, at.y);

    const half = this.map.tileSize / 2;
    const minX = this.map.offsetX + half;
    const maxX = this.map.offsetX + this.map.cols * this.map.tileSize - half;
    const minY = this.map.offsetY + this.map.tileSize;
    const maxY = this.map.offsetY + this.map.rows * this.map.tileSize;

    img.on("drag", (_p: Phaser.Input.Pointer, dragX: number, dragY: number) => {
      img.x = Phaser.Math.Clamp(dragX, minX, maxX);
      img.y = Phaser.Math.Clamp(dragY, minY, maxY);
      img.setDepth(img.y);
    });
    img.on("dragend", () => this.snapMockTower(img, img.x, img.y - half));
    img.on("pointerdown", (p: Phaser.Input.Pointer) => {
      if (p.rightButtonDown()) this.removeMockTower(img);
    });

    this.mockTowers.push(img);
    this.redrawFoliagePreview();
  }

  /** Snaps to the tile containing (wx, wy) and re-applies the real tower depth rule. */
  private snapMockTower(img: Phaser.GameObjects.Image, wx: number, wy: number): void {
    const tile = pixelToTile(this.map, wx, wy);
    const c = Phaser.Math.Clamp(tile.c, 0, this.map.cols - 1);
    const r = Phaser.Math.Clamp(tile.r, 0, this.map.rows - 1);
    const center = tileCenter(this.map, c, r);
    const bottom = center.y + this.map.tileSize / 2;
    img.setPosition(center.x, bottom).setDepth(bottom);
  }

  private removeMockTower(img: Phaser.GameObjects.Image): void {
    this.mockTowers = this.mockTowers.filter((t) => t !== img);
    img.destroy();
    this.redrawFoliagePreview();
  }

  private clearMockTowers(): void {
    for (const t of this.mockTowers) t.destroy();
    this.mockTowers = [];
    this.redrawFoliagePreview();
  }

  private redrawFoliagePreview(): void {
    if (!this.foliageGfx) return;
    this.foliageGfx.clear();

    const proj = getCoverProjection(this.map);
    const toPx = ([u, v]: [number, number]) => ({
      x: proj.left + u * proj.renderWidth,
      y: proj.top + v * proj.renderHeight,
    });

    this.foliageGfx.lineStyle(2, 0x44ff44, 0.85);
    for (const zone of this.draftFoliage) {
      if (isFoliageArea(zone)) {
        const pts = zone.points.map(toPx);
        this.foliageGfx.fillStyle(0x44ff44, 0.12);
        this.foliageGfx.fillPoints(pts, true);
        this.foliageGfx.lineStyle(2, 0x44ff44, 0.85);
        this.foliageGfx.strokePoints(pts, true);
      } else {
        const p = toPx([zone.x, zone.y]);
        this.foliageGfx.strokeCircle(p.x, p.y, zone.r * proj.renderWidth);
      }
    }

    // In-progress polygon: open outline plus a dashed-looking closing hint.
    if (this.pendingArea.length > 0) {
      const pts = this.pendingArea.map(toPx);
      if (pts.length >= 3) {
        this.foliageGfx.fillStyle(THEME.hex.gold, 0.14);
        this.foliageGfx.fillPoints(pts, true);
      }
      this.foliageGfx.lineStyle(2, THEME.hex.gold, 0.95);
      if (pts.length >= 2) this.foliageGfx.strokePoints(pts, pts.length >= 3);
      this.foliageGfx.fillStyle(THEME.hex.gold, 0.9);
      for (const p of pts) this.foliageGfx.fillCircle(p.x, p.y, 3);
    }

    if (this.foliageShape === "brush" && this.foliageBrushWorld) {
      this.foliageGfx.lineStyle(2, THEME.hex.gold, 0.9);
      this.foliageGfx.strokeCircle(this.foliageBrushWorld.x, this.foliageBrushWorld.y, this.foliageRadius);
    }

    const def = CAMPAIGN_MAPS[this.mapIndex];
    const hasSaved = loadFoliageOcclusions(def.key) !== null;
    const saveStr = hasSaved ? "  —  CUSTOM FOLIAGE SAVED" : "";
    const warnStr = !this.mapRenderer.hasBackgroundImage() ? "  —  NO PAINTED BACKGROUND: NO VISUAL EFFECT" : "";
    const count = this.draftFoliage.length;
    const towerStr = this.mockTowers.length > 0 ? `  —  ${this.mockTowers.length} TEST TOWER${this.mockTowers.length === 1 ? "" : "S"}` : "";
    const shapeStr = this.foliageShape === "area"
      ? `AREA  —  ${this.pendingArea.length} PKT (3+ THEN FINISH)`
      : `BRUSH ${Math.round(this.foliageRadius)}px`;
    this.foliageStatusText?.setText(`${count} ZONE${count === 1 ? "" : "S"}  —  ${shapeStr}${towerStr}${saveStr}${warnStr}`).setColor(THEME.colors.gold);
  }

  // ── Path button actions ───────────────────────────────────────────────────

  private toggleLane(): void {
    this.lane = this.lane === "main" ? "alt" : "main";
    this.laneToggleLabel?.setText(`LANE: ${this.lane.toUpperCase()}`);
    this.redrawPathPreview();
  }

  private undoLastPoint(): void {
    const draft = this.activeDraft();
    if (draft.length > 0) { draft.pop(); this.savePathDraft(); }
  }

  private clearCurrentLane(): void {
    if (this.lane === "main") this.draftMain = [];
    else this.draftAlt = [];
    this.savePathDraft();
  }

  private resetPath(): void {
    const def = CAMPAIGN_MAPS[this.mapIndex];
    clearPathOverride(def.key);
    this.draftMain = def.paintedWaypoints ? [...def.paintedWaypoints] : [];
    this.draftAlt = def.altPaintedWaypoints ? [...def.altPaintedWaypoints] : [];
    this.map = buildMapFromDef(def);
    this.redrawPathPreview();
  }

  /** Prepend a point ~40 px off-screen along the reversed first segment of the active lane. */
  private extendSpawn(): void {
    const draft = this.activeDraft();
    if (draft.length < 2) return;
    const proj = getCoverProjection(this.map);
    const [x0, y0] = draft[0];
    const [x1, y1] = draft[1];
    const dx = x0 - x1;
    const dy = y0 - y1;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) return;
    draft.unshift([x0 + (40 / proj.renderWidth) * (dx / len), y0 + (40 / proj.renderHeight) * (dy / len)]);
    this.savePathDraft();
  }

  private handleExport(): void {
    if (this.editorMode === "mask") this.exportMasks();
    else if (this.editorMode === "path") this.exportPaths();
    else this.exportFoliage();
  }

  private exportPaths(): void {
    const snippet = exportCustomPaths();
    if (!snippet) {
      this.pathStatusText?.setText("NO CUSTOM PATHS TO EXPORT").setColor(THEME.colors.parchmentDim);
      return;
    }
    navigator.clipboard?.writeText(snippet).catch(() => {});
    const blob = new Blob([snippet], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "path-overrides-export.txt"; a.click();
    URL.revokeObjectURL(url);
    this.pathStatusText?.setText("PATHS EXPORTED  —  COPIED TO CLIPBOARD").setColor(THEME.colors.frost);
  }

  private exportFoliage(): void {
    const snippet = exportCustomFoliage();
    if (!snippet) {
      this.foliageStatusText?.setText("NO CUSTOM FOLIAGE TO EXPORT").setColor(THEME.colors.parchmentDim);
      return;
    }
    navigator.clipboard?.writeText(snippet).catch(() => {});
    const blob = new Blob([snippet], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "foliage-occlusions-export.txt"; a.click();
    URL.revokeObjectURL(url);
    this.foliageStatusText?.setText("FOLIAGE EXPORTED  —  COPIED TO CLIPBOARD").setColor(THEME.colors.frost);
  }

  // ── Mask editor ───────────────────────────────────────────────────────────

  private setCell(c: number, r: number, value: boolean): void {
    if (this.mask[r][c] === value) return;
    this.mask[r][c] = value;
    this.custom = true;
    savePlacementMask(CAMPAIGN_MAPS[this.mapIndex].key, this.mask);
    this.refreshCell(c, r);
    this.refreshStatus();
  }

  private refreshCell(c: number, r: number): void {
    const cell = this.cells[r][c];
    const allowed = this.mask[r][c];
    // Blocked tiles get no overlay — they read as plain map terrain (as in-game).
    cell.bg
      .setFillStyle(0x2d8a45, allowed ? 0.24 : 0)
      .setStrokeStyle(1, 0xa4e8ae, allowed ? 0.55 : 0);
    cell.icon.setVisible(allowed);
  }

  private refreshAllCells(): void {
    for (let r = 0; r < this.map.rows; r++)
      for (let c = 0; c < this.map.cols; c++) this.refreshCell(c, r);
    this.refreshStatus();
  }

  private refreshStatus(): void {
    const allowed = this.mask.flat().filter(Boolean).length;
    this.statusText?.setText(this.custom ? "CUSTOM MASK • SAVED" : "DEFAULT MAP RULES");
    this.statusText?.setColor(this.custom ? THEME.colors.gold : THEME.colors.frost);
    this.countText?.setText(`${allowed} allowed  •  ${this.map.cols * this.map.rows - allowed} blocked  •  Map ${this.mapIndex + 1}/${CAMPAIGN_MAPS.length}`);
  }

  private fill(value: boolean): void {
    this.mask = Array.from({ length: this.map.rows }, () => Array(this.map.cols).fill(value));
    this.custom = true;
    savePlacementMask(CAMPAIGN_MAPS[this.mapIndex].key, this.mask);
    this.refreshAllCells();
  }

  private resetDefault(): void {
    const def = CAMPAIGN_MAPS[this.mapIndex];
    clearPlacementMask(def.key);
    const cleanMap = buildMapFromDef(def);
    this.mask = defaultPlacementMask(cleanMap);
    this.custom = false;
    this.refreshAllCells();
  }

  /** Editor-only draggable archer tower to preview placement/scale. Never touches mask or real game state. */
  private toggleTowerPreview(): void {
    if (this.previewTower) {
      this.previewTower.destroy();
      this.previewTower = undefined;
      this.towerButtonLabel?.setText("PLACE TOWER");
      return;
    }

    const startC = Math.floor(this.map.cols / 2);
    const startR = Math.floor(this.map.rows / 2);
    const start = tileCenter(this.map, startC, startR);
    const minX = this.map.offsetX + this.map.tileSize / 2;
    const maxX = this.map.offsetX + this.map.cols * this.map.tileSize - this.map.tileSize / 2;
    const minY = this.map.offsetY + this.map.tileSize / 2;
    const maxY = this.map.offsetY + this.map.rows * this.map.tileSize - this.map.tileSize / 2;

    const icon = this.add.image(start.x, start.y, "tower-archer-1")
      .setDisplaySize(this.map.tileSize * 1.04, this.map.tileSize * 1.04)
      .setDepth(THEME.depths.units)
      .setInteractive({ useHandCursor: true, draggable: true });
    this.input.setDraggable(icon);

    icon.on("drag", (_pointer: Phaser.Input.Pointer, dragX: number, dragY: number) => {
      icon.x = Phaser.Math.Clamp(dragX, minX, maxX);
      icon.y = Phaser.Math.Clamp(dragY, minY, maxY);
    });
    icon.on("dragend", () => {
      const tile = pixelToTile(this.map, icon.x, icon.y);
      const c = Phaser.Math.Clamp(tile.c, 0, this.map.cols - 1);
      const r = Phaser.Math.Clamp(tile.r, 0, this.map.rows - 1);
      const center = tileCenter(this.map, c, r);
      this.tweens.add({ targets: icon, x: center.x, y: center.y, duration: 120, ease: "Sine.easeOut" });
    });

    this.previewTower = icon;
    this.towerButtonLabel?.setText("REMOVE TOWER");
  }

  /** Downloads + copies every browser-local mask override for pasting into PlacementMasks.ts. */
  private exportMasks(): void {
    const snippet = exportCustomMasks();
    if (!snippet) {
      this.statusText?.setText("NOTHING TO EXPORT");
      this.statusText?.setColor(THEME.colors.blood);
      return;
    }
    const blob = new Blob([snippet + "\n"], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "placement-masks-export.txt";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    navigator.clipboard?.writeText(snippet).catch(() => {});
    this.statusText?.setText("EXPORTED — file + clipboard");
    this.statusText?.setColor(THEME.colors.frost);
  }

  private openMap(index: number): void {
    this.closeOverlay();
    this.scene.restart({ mapIndex: Phaser.Math.Wrap(index, 0, CAMPAIGN_MAPS.length), mode: this.editorMode });
  }

  private playTest(): void {
    const def = CAMPAIGN_MAPS[this.mapIndex];
    this.closeOverlay();
    this.registry.set("selectedMap", def.key);
    this.registry.set("selectedDifficulty", 1);
    this.registry.set("runTutorial", false);
    // Lets UIScene offer "BACK TO EDITOR" and restores this exact map + mode.
    this.registry.set("editorPlayTest", true);
    this.registry.set("editorMapIndex", this.mapIndex);
    this.registry.set("editorMode", this.editorMode);
    goTo(this, "GameScene", { launch: ["UIScene"] });
  }
}
